import React, { FC, useCallback, useEffect, useReducer, useState } from 'react'
import {
    VespaRecommendedNotificationContext,
    VespaRecommendedNotificationDispatchContext,
    vespaRecommendedNotificationReducer,
} from './reducer'
import {
    VespaRecommendationConfiguration,
    VespaRecommendedNotificationParams,
} from '@pushly/models/lib/structs/journeys'
import { VespaConfigurationEditor } from './configuration/vespa-configuration-editor'
import { VespaConfigConnector } from './configuration/vespa-config-connector'
import { getRandId } from '@pushly/models/lib/utils/get-rand-id'
import {
    defaultVespaConfigurationValue,
    INDEX_DISPLAY_DEFAULT_ID,
    MAX_VESPA_RECOMMENDATION_CONFIGS_ALLOWED,
} from './constants'
import { RecommendationConfigurationEditor } from '../shared'
import { PropertiesOf } from '@pushly/models/lib/types'
import { Flex } from 'antd'

type VespaRecommendationEditorProps = {
    vespaParams: VespaRecommendedNotificationParams
    onChange: (change: Partial<PropertiesOf<VespaRecommendedNotificationParams>>) => void
    readonly?: boolean
}

export const VespaRecommendationEditor: FC<VespaRecommendationEditorProps> = ({
    onChange,
    vespaParams,
    readonly = false,
}) => {
    const [context, dispatch] = useReducer(vespaRecommendedNotificationReducer, {
        source: vespaParams,
        configs: new Map(
            vespaParams.recommendation_config?.map((config) => [getRandId(), config]) ?? [
                [
                    getRandId(),
                    // Uses the same default (Balanced weights + 12h Content
                    // Window) as handleAddConfig below — see
                    // defaultVespaConfigurationValue in constants.ts for the
                    // full rationale.
                    VespaRecommendationConfiguration.from({
                        ...defaultVespaConfigurationValue,
                        id: INDEX_DISPLAY_DEFAULT_ID[0],
                    }),
                ],
            ],
        ),
    })

    const [latestAdd, setLatestAdd] = useState<string | null>(null)

    useEffect(() => {
        onChange({ recommendation_config: Array.from(context.configs.values()) })
    }, [context])

    useEffect(() => {
        if (latestAdd) {
            const newConfigEl = document.getElementById(latestAdd)
            if (newConfigEl) {
                newConfigEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
        }
    }, [latestAdd])

    const handleAddConfig = useCallback(
        (config?: VespaRecommendationConfiguration) => {
            if (context.configs.size >= MAX_VESPA_RECOMMENDATION_CONFIGS_ALLOWED) return

            const newConfigRecordId = getRandId()

            dispatch({
                type: 'add-configuration',
                recordId: newConfigRecordId,
                configuration: VespaRecommendationConfiguration.from({
                    ...defaultVespaConfigurationValue,
                    ...config,
                    id: INDEX_DISPLAY_DEFAULT_ID[context.configs.size],
                    count: 1,
                }),
            })

            setLatestAdd(newConfigRecordId)
        },
        [context.configs.size],
    )

    return (
        <RecommendationConfigurationEditor
            id="vespa-recommendation-notification-editor"
            readonly={readonly}
            configCount={context.configs.size}
            maxConfigs={MAX_VESPA_RECOMMENDATION_CONFIGS_ALLOWED}
            onAdd={() => handleAddConfig()}
        >
            <VespaRecommendedNotificationContext.Provider value={context}>
                <VespaRecommendedNotificationDispatchContext.Provider value={dispatch}>
                    {Array.from(context.configs.entries()).map(([key, configuration], index) => (
                        <React.Fragment key={key}>
                            {index > 0 && <VespaConfigConnector />}
                            <VespaConfigurationEditor
                                configuration={{ recordId: key, configuration }}
                                handleAdd={handleAddConfig}
                            />
                        </React.Fragment>
                    ))}
                </VespaRecommendedNotificationDispatchContext.Provider>
            </VespaRecommendedNotificationContext.Provider>
        </RecommendationConfigurationEditor>
    )
}
